import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import { LIST_ALLOWED_TIMES_RESOURCE } from 'src/features/membershipType/api/allowedTime/useListMembershipTypeAllowedTimes';
import AllowedTimeFormModal from 'src/features/membershipType/components/forms/allowedTime/AllowedTimeFormModal';
import { allowedTimesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { useParking } from 'src/providers/ParkingProvider';

interface CreateAllowedTimeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateAllowedTimeFormModal<TData>({ className }: CreateAllowedTimeFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: membershipType } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });

    const { mutate: mutateAllowedTimes, isPending } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOWED_TIMES_RESOURCE] });
            toast({
                title: formatMessage(allowedTimesMessages.createAllowedTimeSuccess),
                description: formatMessage(allowedTimesMessages.createAllowedTimeSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <AllowedTimeFormModal
            allowedTimes={membershipType?.membership_type_allowed_times}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutateAllowedTimes(
                    { parkingId, id: membershipTypeId, membership_type_allowed_times: data },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(allowedTimesMessages.allowedTime).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(allowedTimesMessages.addAllowedTimeModalSubtitle)}
        />
    );
}

export default CreateAllowedTimeFormModal;
